import {
  Button,
  Dropdown,
  Option,
  Spinner,
  Switch,
  Tab,
  TabList,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import {
  Add24Regular,
  Dismiss24Regular,
  Play24Filled,
  Save24Regular,
} from '@fluentui/react-icons';
import { useEffect, useRef, useState } from 'react';

import type { SavedConfig } from '../../../types/caller.js';
import { renderVarHighlight } from '../VarHighlight/VarHighlight.js';

import type { UrlBarProps } from './UrlBar.interface.js';
import { useStyles } from './UrlBar.style.js';

const METHODS: SavedConfig['method'][] = [
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
];

export function UrlBar({
  openTabs,
  activeTabId,
  savedConfigs,
  activeConfig,
  emitSigsLocally,
  loading,
  onTabSelect,
  onCloseTab,
  onNewTab,
  onUrlChange,
  onMethodChange,
  onSave,
  onRun,
  onToggleEmitSigs,
}: UrlBarProps) {
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);

  // Sync horizontal scroll between the hidden input and the highlight backdrop
  useEffect(() => {
    const input = inputRef.current;
    const backdrop = backdropRef.current;
    if (!input || !backdrop) return;
    const syncScroll = () => {
      backdrop.scrollLeft = input.scrollLeft;
    };
    input.addEventListener('scroll', syncScroll);
    input.addEventListener('keyup', syncScroll);
    return () => {
      input.removeEventListener('scroll', syncScroll);
      input.removeEventListener('keyup', syncScroll);
    };
  }, []);

  function getTabLabel(configId: string): string {
    const saved = savedConfigs.find((c) => c.id === configId);
    return saved?.name ?? 'New Request';
  }

  const url = activeConfig?.url ?? '';
  const vars = activeConfig?.vars ?? [];

  // Shared text metrics — must match exactly between backdrop and input
  const fontFamily = tokens.fontFamilyBase;
  const fontSize = tokens.fontSizeBase200;
  const inputPadding = '0 8px';

  return (
    <div className={styles.root}>
      {/* Tab strip */}
      <div className={styles.tabStrip}>
        <TabList
          selectedValue={activeTabId ?? undefined}
          onTabSelect={(_, d) => onTabSelect(d.value as string)}
          size="small"
        >
          {openTabs.map((tab) => (
            <Tab key={tab.configId} value={tab.configId}>
              <div className={styles.tabLabel}>
                <span>{getTabLabel(tab.configId)}</span>
                {tab.isDirty && <span style={{ color: 'orange' }}>●</span>}
                <Tooltip content="Close tab" relationship="label">
                  <span
                    role="button"
                    tabIndex={0}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCloseTab(tab.configId);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        onCloseTab(tab.configId);
                      }
                    }}
                  >
                    <Dismiss24Regular
                      style={{ width: '12px', height: '12px' }}
                    />
                  </span>
                </Tooltip>
              </div>
            </Tab>
          ))}
        </TabList>
        <Tooltip content="New tab" relationship="label">
          <Button
            appearance="subtle"
            icon={<Add24Regular />}
            size="small"
            onClick={onNewTab}
          />
        </Tooltip>
      </div>

      {/* URL row */}
      <div className={styles.urlRow}>
        <Dropdown
          value={activeConfig?.method ?? 'POST'}
          onOptionSelect={(_, d) =>
            onMethodChange(d.optionValue as SavedConfig['method'])
          }
          style={{ minWidth: '90px' }}
          size="small"
        >
          {METHODS.map((m) => (
            <Option key={m} value={m}>
              {m}
            </Option>
          ))}
        </Dropdown>

        {/* Overlay URL input */}
        <div
          style={{
            position: 'relative',
            flex: 1,
            height: '26px',
            borderRadius: tokens.borderRadiusMedium,
            border: focused
              ? `1px solid ${tokens.colorBrandStroke1}`
              : `1px solid ${tokens.colorNeutralStroke1}`,
          }}
        >
          {/* Backdrop with highlighted {{vars}} */}
          <div
            ref={backdropRef}
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              padding: inputPadding,
              fontFamily,
              fontSize,
              letterSpacing: 0,
              wordSpacing: 0,
              lineHeight: '26px',
              whiteSpace: 'pre',
              overflow: 'hidden',
              pointerEvents: 'none',
              backgroundColor: tokens.colorNeutralBackground1,
              color: tokens.colorNeutralForeground1,
              borderRadius: tokens.borderRadiusMedium,
              boxSizing: 'border-box',
            }}
          >
            {url ? (
              renderVarHighlight(url, vars)
            ) : (
              <span style={{ color: tokens.colorNeutralForeground4 }}>
                https://...
              </span>
            )}
          </div>

          {/* Actual input — transparent text, visible caret */}
          <input
            ref={inputRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              padding: inputPadding,
              fontFamily,
              fontSize,
              letterSpacing: 0,
              wordSpacing: 0,
              backgroundColor: 'transparent',
              color: 'transparent',
              caretColor: tokens.colorNeutralForeground1,
              border: 'none',
              outline: 'none',
              zIndex: 1,
              boxSizing: 'border-box',
            }}
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </div>

        <Tooltip content="Save configuration" relationship="label">
          <Button
            appearance="subtle"
            icon={<Save24Regular />}
            size="small"
            onClick={onSave}
          >
            Save
          </Button>
        </Tooltip>

        <Button
          appearance="primary"
          icon={loading ? <Spinner size="tiny" /> : <Play24Filled />}
          size="small"
          onClick={onRun}
          disabled={loading || !activeConfig?.url}
        >
          Run
        </Button>

        <div className={styles.sigsToggle}>
          <Switch
            checked={emitSigsLocally}
            onChange={(_, d) => onToggleEmitSigs(d.checked)}
            label="Emit SIGS locally"
          />
        </div>
      </div>
    </div>
  );
}
